import React, { useEffect, useRef, useState } from 'react';
import { Check, CheckCircle2, Pencil, X } from 'lucide-react';
import { ConfidencePill, CONFIDENCE_THRESHOLD, Pill } from './ui';
import type { FieldAction } from '../titleSearch/review';

/**
 * FieldCard: one extracted field next to the Document_Viewer.
 *
 * Variants
 * - `confirm` (Due Diligence / Borrower Analysis, Extract mode): a Confirm toggle.
 * - `review` (Title Search, Review mode): Accept / Edit / Reject, with inline edit.
 *
 * Review states: no action (gray), below threshold (red hint), accepted (green),
 * edited (violet, shows "Extracted: ..."), rejected (value struck through).
 *
 * The card is controlled: the parent owns confirmation / action and the value.
 * Only the inline edit buffer is local. Light theme only.
 */
export type FieldCardVariant = 'confirm' | 'review';

export interface FieldCardProps {
  variant: FieldCardVariant;
  label: string;
  /** Current value. For an edited field, this is the edited value. */
  value: string;
  /** Confidence score from 0 to 1. */
  confidence: number | null | undefined;
  /** 1-based source page. */
  page?: number | null;
  /** Source document name. */
  source?: string;
  /**
   * Why this field needs attention (conflict with another document, read with
   * low confidence). Shown until the field is confirmed or has an action.
   */
  note?: string;
  /** Highlights the card as the selected one. */
  selected?: boolean;
  onSelect?: () => void;
  /**
   * Position of the field in the list. Shown as a badge that matches the one
   * on the field's highlight in the document viewer.
   */
  number?: number;

  /** confirm variant: whether the field is confirmed. */
  confirmed?: boolean;
  onConfirm?: () => void;
  /**
   * confirm variant: value as extracted. Shown as "Extracted: …" once the
   * reviewer has edited the value, so the original is kept visible.
   */
  extractedValue?: string;

  /** review variant: the reviewer's action, if any. */
  action?: FieldAction | null;
  onAccept?: () => void;
  /** Called with the new value when the reviewer saves an inline edit. */
  onEdit?: (value: string) => void;
  onReject?: () => void;

  /**
   * review variant: show the stored action and value without any control (a
   * finalized review). A field with no action is shown plainly, with no
   * below-threshold hint, since nothing is waiting on the reader.
   */
  readOnly?: boolean;

  /** Percentage below which a value needs checking. Defaults to 0.8. */
  threshold?: number;
  className?: string;
}

type Tone = 'neutral' | 'danger' | 'success' | 'edited';

const CARD_TONE: Record<Tone, string> = {
  neutral: 'border-gray-200',
  danger: 'border-[#fecaca]',
  success: 'border-[#bbf7d0]',
  edited: 'border-[#d4c6ff]',
};

const ACTION_BASE =
  'inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-xs font-bold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB] focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50';
const ACTION_IDLE = 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50';

export const FieldCard: React.FC<FieldCardProps> = ({
  variant,
  label,
  value,
  confidence,
  page,
  source,
  note,
  selected = false,
  onSelect,
  number,
  confirmed = false,
  onConfirm,
  action = null,
  extractedValue,
  onAccept,
  onEdit,
  onReject,
  readOnly = false,
  threshold = CONFIDENCE_THRESHOLD,
  className = '',
}) => {
  const isReview = variant === 'review';
  const [editing, setEditing] = useState(false);
  const [buffer, setBuffer] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const hasScore = typeof confidence === 'number' && !Number.isNaN(confidence);
  const belowThreshold = !hasScore || (confidence as number) < threshold;
  const percent = hasScore ? Math.round((confidence as number) * 100) : null;

  const resolved = isReview ? action !== null && action !== undefined : confirmed;
  const needsCheck = isReview && !readOnly && belowThreshold && !resolved;
  const showNote = Boolean(note) && !resolved;

  // Confirm variant: an edit replaces the stored value, so a non-empty
  // `extractedValue` that differs from the current value means the field has
  // been edited.
  const edited =
    !isReview &&
    extractedValue !== undefined &&
    extractedValue !== null &&
    extractedValue !== value;

  let tone: Tone = 'neutral';
  if (isReview) {
    if (action === 'accept') tone = 'success';
    else if (action === 'edit') tone = 'edited';
    else if (needsCheck) tone = 'danger';
  } else if (edited) {
    tone = 'edited';
  } else if (confirmed) {
    tone = 'success';
  }

  const startEdit = () => {
    setBuffer(value);
    setEditing(true);
  };
  const cancelEdit = () => {
    setEditing(false);
    setBuffer(value);
  };
  const saveEdit = () => {
    if (!buffer.trim()) return;
    onEdit?.(buffer);
    setEditing(false);
  };

  const selectedClasses = selected
    ? 'border-l-[3px] border-l-[#834DFB] bg-[#F5F3FF]'
    : 'bg-white';
  const rejected = isReview && action === 'reject';
  const showExtracted =
    (isReview &&
      extractedValue !== undefined &&
      (action === 'edit' || (action === 'accept' && extractedValue !== value))) ||
    (!isReview && edited);

  const metaParts: string[] = [];
  if (source) metaParts.push(source);
  if (page) metaParts.push(`Page ${page}`);

  return (
    <div
      data-testid="field-card"
      data-selected={selected ? 'true' : 'false'}
      onClick={onSelect}
      className={`rounded-xl border p-3.5 ${CARD_TONE[tone]} ${selectedClasses} ${className}`.trim()}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onSelect?.();
        }}
        aria-pressed={selected}
        aria-label={`Select field ${label}`}
        className="block w-full rounded-md text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB]"
      >
        <span className="flex items-center justify-between gap-2">
          <span className="flex min-w-0 items-center gap-1.5">
            {number !== undefined ? (
              <span
                data-testid="field-number"
                aria-hidden="true"
                className="inline-flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-full bg-[#EDE7FF] px-1 text-[10px] font-bold leading-none text-[#5A21C9]"
              >
                {number}
              </span>
            ) : null}
            <span className="text-xs font-bold text-gray-500">{label}</span>
          </span>
          <ConfidencePill score={confidence} showPercent={isReview} />
        </span>
        {!editing && (
          <span
            className={`mt-1 block break-words text-[15px] font-bold ${
              rejected ? 'text-gray-400 line-through' : 'text-gray-900'
            }`}
          >
            {value || <span className="font-normal italic text-gray-500">Empty</span>}
          </span>
        )}
        {metaParts.length > 0 && (
          <span className="mt-0.5 block text-xs text-gray-500">{metaParts.join(' · ')}</span>
        )}
      </button>

      {editing && (
        <div className="mt-2 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <input
            ref={inputRef}
            type="text"
            value={buffer}
            aria-label={`Edit ${label}`}
            onChange={(e) => setBuffer(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                saveEdit();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                cancelEdit();
              }
            }}
            className="min-w-0 flex-1 rounded-lg border border-[#834DFB] bg-white px-2.5 py-1.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#834DFB]/30"
          />
          <button
            type="button"
            onClick={saveEdit}
            disabled={!buffer.trim()}
            title={buffer.trim() ? undefined : 'Enter a value to save'}
            className={`${ACTION_BASE} border-[#834DFB] bg-[#834DFB] text-white hover:bg-[#6E2EF0]`}
          >
            Save
          </button>
          <button type="button" onClick={cancelEdit} className={`${ACTION_BASE} ${ACTION_IDLE}`}>
            Cancel
          </button>
        </div>
      )}

      {showExtracted && !editing && (
        <p className="mt-1 break-words text-xs text-gray-500">Extracted: {extractedValue}</p>
      )}

      {needsCheck && (
        <p className="mt-2 rounded-lg border border-[#fecaca] bg-[#fef2f2] px-2.5 py-1.5 text-xs text-[#991b1b]">
          {percent !== null ? `Confidence ${percent}%` : 'Confidence unknown'}
          {`, below ${Math.round(threshold * 100)}%. Check this value against ${
            page ? `page ${page}` : 'the page'
          }.`}
        </p>
      )}

      {showNote && (
        <p
          role="note"
          className="mt-2 rounded-lg border border-[#fef08a] bg-[#fefce8] px-2.5 py-1.5 text-xs text-[#854d0e]"
        >
          {note}
        </p>
      )}

      {/* A finalized review is read-only, except that the owner may still allow
          correcting a value (an `onEdit` handler): only Edit is offered then. */}
      {isReview && readOnly && (action || (onEdit && !editing)) && (
        <div
          className="mt-2.5 flex items-center justify-between gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          {action ? (
            <Pill tone={action === 'accept' ? 'success' : action === 'edit' ? 'selected' : 'danger'}>
              {action === 'accept' ? 'Accepted' : action === 'edit' ? 'Edited' : 'Rejected'}
            </Pill>
          ) : (
            <span />
          )}
          {onEdit && !editing && (
            <button
              type="button"
              onClick={startEdit}
              aria-label={`Edit ${label}`}
              className={`${ACTION_BASE} ${ACTION_IDLE}`}
            >
              <Pencil size={14} strokeWidth={2} aria-hidden="true" />
              Edit
            </button>
          )}
        </div>
      )}

      {!editing && !(isReview && readOnly) && (
        <div
          className="mt-2.5 flex flex-wrap items-center gap-1.5"
          onClick={(e) => e.stopPropagation()}
        >
          {isReview ? (
            <>
              <button
                type="button"
                aria-pressed={action === 'accept'}
                onClick={onAccept}
                className={`${ACTION_BASE} ${
                  action === 'accept'
                    ? 'border-[#bbf7d0] bg-[#dcfce7] text-[#166534]'
                    : ACTION_IDLE
                }`}
              >
                <Check size={14} strokeWidth={2} aria-hidden="true" />
                Accept
              </button>
              <button
                type="button"
                aria-pressed={action === 'edit'}
                onClick={startEdit}
                className={`${ACTION_BASE} ${
                  action === 'edit'
                    ? 'border-[#d4c6ff] bg-[#EDE7FF] text-[#5A21C9]'
                    : ACTION_IDLE
                }`}
              >
                <Pencil size={14} strokeWidth={2} aria-hidden="true" />
                Edit
              </button>
              {/* Reject is offered only when the owner handles it. Title Search
                  review does not: the reviewer must enter the correct value. */}
              {onReject && (
                <button
                  type="button"
                  aria-pressed={action === 'reject'}
                  onClick={onReject}
                  className={`${ACTION_BASE} ${
                    action === 'reject'
                      ? 'border-[#fecaca] bg-[#fee2e2] text-[#991b1b]'
                      : ACTION_IDLE
                  }`}
                >
                  <X size={14} strokeWidth={2} aria-hidden="true" />
                  Reject
                </button>
              )}
              {action && (
                <Pill
                  tone={
                    action === 'accept' ? 'success' : action === 'edit' ? 'selected' : 'danger'
                  }
                  className="ml-auto"
                >
                  {action === 'accept' ? 'Accepted' : action === 'edit' ? 'Edited' : 'Rejected'}
                </Pill>
              )}
            </>
          ) : (
            <>
              <button
                type="button"
                aria-pressed={confirmed}
                onClick={onConfirm}
                className={`${ACTION_BASE} ${
                  confirmed ? 'border-[#bbf7d0] bg-[#dcfce7] text-[#166534]' : ACTION_IDLE
                }`}
              >
                {confirmed ? (
                  <CheckCircle2 size={14} strokeWidth={2} aria-hidden="true" />
                ) : (
                  <Check size={14} strokeWidth={2} aria-hidden="true" />
                )}
                {confirmed ? 'Confirmed' : 'Confirm'}
              </button>
              {onEdit && (
                <button
                  type="button"
                  onClick={startEdit}
                  aria-label={`Edit ${label}`}
                  className={`${ACTION_BASE} ${
                    edited ? 'border-[#d4c6ff] bg-[#EDE7FF] text-[#5A21C9]' : ACTION_IDLE
                  }`}
                >
                  <Pencil size={14} strokeWidth={2} aria-hidden="true" />
                  Edit
                </button>
              )}
              {edited && <Pill tone="selected" className="ml-auto">Edited</Pill>}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default FieldCard;
